import { ComponentConfig } from "@measured/puck";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ScoreSelectField from "../../fields/ScoreSelectField";
import VariationSelectField from "../../fields/VariationSelectField";
import ScoreChartblock, { ScoreChartblockProps } from "./ScoreChartBlock";

type ScoreSelection = {
    scoreId: number | null;
    variationId: string | null;
    ssqId: number | null;
};

type ScoreSelectionFieldProps = {
    value: ScoreSelection | undefined;
    onChange: (value: ScoreSelection) => void;
};

const ScoreSelectionField = (props: ScoreSelectionFieldProps) => {
    const { value, onChange } = props;
    const theme = useTheme();
    const selection: ScoreSelection = value || {
        scoreId: null,
        variationId: null,
        ssqId: null,
    };

    const update = (patch: Partial<ScoreSelection>) => {
        const next = { ...selection, ...patch };
        onChange(next);
    };

    return (
        <Stack spacing={theme.spacing(2)}>
            <ScoreSelectField
                value={selection.scoreId}
                onChange={(v) => {
                    update({ scoreId: v || null, variationId: null, ssqId: null });
                }}
                field={{ type: "custom", render: () => <></> }}
                name="scoreChartblock.scoreId"
                id="scoreChartblock-score"
            />

            {selection.scoreId ?
                <VariationSelectField
                    scoreId={selection.scoreId}
                    value={selection.variationId}
                    onChange={(v) => update({ variationId: v })}
                    field={{ type: "custom", render: () => <></> }}
                    name="scoreChartblock.variationId"
                    id="scoreChartblock-variation"
                />
            :   <></>}
        </Stack>
    );
};

export type ScoreChartblockConfigProps = {
    scoreConfig?: {
        config?: ScoreSelection;
    };
    showTitle?: "true" | "false";
    showUnitSubtitle?: "true" | "false";
    title?: string;
    chartType?: "auto" | "donut" | "bar" | "line" | "cumulative" | "pie" | "radar" | "table" | "raw";
    showLegend?: "true" | "false";
    legendPosition?: "bottom" | "right";
    showTooltip?: "true" | "false";
    spacing?: number;
    borderWidth?: number;
};

export const ScoreChartblockConfig: ComponentConfig<ScoreChartblockConfigProps> = {
    label: "Score Chart",
    fields: {
        chartType: {
            type: "select",
            label: "Chart type",
            options: [
                { label: "Auto", value: "auto" },
                { label: "Donut", value: "donut" },
                { label: "Bar", value: "bar" },
                { label: "Line", value: "line" },
                { label: "Cumulative", value: "cumulative" },
                { label: "Pie", value: "pie" },
                { label: "Radar", value: "radar" },
                { label: "Table", value: "table" },
                { label: "Raw", value: "raw" },
            ],
        },
        scoreConfig: {
            type: "object",
            label: "Score",
            objectFields: {
                config: {
                    type: "custom",
                    render: (props: ScoreSelectionFieldProps) => (
                        <ScoreSelectionField value={props.value} onChange={props.onChange} />
                    ),
                },
            },
        },
        showTitle: {
            type: "select",
            label: "Show title",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
        showUnitSubtitle: {
            type: "select",
            label: "Show unit subtitle",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
        title: {
            type: "text",
            label: "Title override",
        },
        showLegend: {
            type: "select",
            label: "Show legend",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
        legendPosition: {
            type: "select",
            label: "Legend position",
            options: [
                { label: "Bottom", value: "bottom" },
                { label: "Right", value: "right" },
            ],
        },
        showTooltip: {
            type: "select",
            label: "Show tooltip",
            options: [
                { label: "Yes", value: "true" },
                { label: "No", value: "false" },
            ],
        },
        spacing: {
            type: "number",
            label: "Slice spacing",
            min: 0,
        },
        borderWidth: {
            type: "number",
            label: "Border width",
            min: 0,
        },
    },
    defaultProps: {
        scoreConfig: {
            config: {
                scoreId: null,
                variationId: null,
                ssqId: null,
            },
        },
        showTitle: "true",
        showUnitSubtitle: "false",
        title: "",
        chartType: "auto",
        showLegend: "false",
        legendPosition: "bottom",
        showTooltip: "true",
        spacing: 2,
        borderWidth: 2,
    },
    render: (props: ScoreChartblockConfigProps) => {
        const scoreId = props.scoreConfig?.config?.scoreId ?? null;
        const rawVariationId = props.scoreConfig?.config?.variationId ?? null;
        const variationId = rawVariationId ? Number(rawVariationId) : null;
        const ssqId = props.scoreConfig?.config?.ssqId ?? null;

        const renderProps: ScoreChartblockProps = {
            scoreId,
            variationId,
            ssqId,
            showTitle: props.showTitle !== "false",
            showUnitSubtitle: props.showUnitSubtitle === "true",
            title: props.title,
            chartType: props.chartType ?? "auto",
            showLegend: props.showLegend === "true",
            legendPosition: props.legendPosition ?? "bottom",
            showTooltip: props.showTooltip !== "false",
            spacing: props.spacing,
            borderWidth: props.borderWidth,
        };

        return <ScoreChartblock {...renderProps} />;
    },
};

export default ScoreChartblockConfig;
